import * as React from "react";
import { cn } from "@/lib/utils";

const SIZE_PRESETS = {
  xs: {
    ring: 52,
    stroke: 5,
    percentClass: "px-0.5 text-center text-[12px] font-bold tabular-nums leading-none text-neutral-900",
    labelClass: "px-0.5 text-center text-[7px] font-semibold uppercase leading-tight text-neutral-600",
  },
  sm: {
    ring: 68,
    stroke: 7,
    percentClass: "text-sm font-semibold text-neutral-900",
    labelClass: "text-[8px] font-semibold uppercase text-neutral-600",
  },
  md: {
    ring: 96,
    stroke: 10,
    percentClass: "text-2xl font-semibold text-neutral-900",
    labelClass: "mt-0.5 text-[10px] font-semibold uppercase text-neutral-700",
  },
  lg: {
    ring: 120,
    stroke: 12,
    percentClass: "text-3xl font-semibold text-foreground",
    labelClass: "mt-1 text-[11px] font-semibold uppercase text-foreground",
  },
} as const;

export type SuperPageAssignmentsProgressRingSize = keyof typeof SIZE_PRESETS;

export type SuperPageAssignmentsProgressRingProps = {
  completedCount: number;
  total: number;
  size?: SuperPageAssignmentsProgressRingSize;
  className?: string;
  skeleton?: boolean;
  variant?: "default" | "compact";
};

const SuperPageAssignmentsProgressRing: React.FC<SuperPageAssignmentsProgressRingProps> = ({
  completedCount,
  total,
  size = "lg",
  className,
  skeleton,
  variant = "default",
}) => {
  const { ring, stroke, percentClass, labelClass } = SIZE_PRESETS[size];
  const radius = (ring - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const completionRate = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const progressDashOffset = circumference * (1 - completionRate / 100);

  const isCompact = variant === "compact";

  return (
    <div
      className={cn("relative shrink-0 overflow-hidden rounded-full", className)}
      style={{ width: ring, height: ring }}
    >
      <svg
        width={ring}
        height={ring}
        className="-rotate-90 block stroke-gray-200"
        aria-hidden
        data-skeleton={skeleton ? true : undefined}
      >
        <circle cx={ring / 2} cy={ring / 2} r={radius} fill="none" strokeWidth={stroke} />
        <circle
          cx={ring / 2}
          cy={ring / 2}
          r={radius}
          fill="none"
          className={completionRate === 100 ? "stroke-emerald-500" : "stroke-amber-500"}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={progressDashOffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none">
        <div className={percentClass} data-skeleton={skeleton ? true : undefined}>
          {completionRate}%
        </div>
        {!isCompact && (
          <div className={labelClass} data-skeleton={skeleton ? true : undefined}>
            Complete
          </div>
        )}
      </div>
    </div>
  );
};

export { SuperPageAssignmentsProgressRing };
